import React, { useEffect, useId, useRef } from "react";
import { X } from "lucide-react";

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Accessible dialog: role="dialog" + aria-modal, focus moves in on open and is
// restored on close, Tab is trapped, Escape and backdrop click close it, and
// page scroll is locked while open. Rendered in place (not a portal) so the
// TSP styles under .tsp-app apply. Do not nest two modals — close one first.
export default function TspModal({ open, title, onClose, children, footer, size = "md" }) {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const dialog = dialogRef.current;
    const focusables = () => (dialog ? [...dialog.querySelectorAll(FOCUSABLE)] : []);
    (focusables()[0] || dialog)?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="tsp-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div ref={dialogRef} className={`tsp-modal tsp-modal-${size}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className="tsp-modal-header">
          <h3 id={titleId}>{title}</h3>
          <button type="button" className="tsp-modal-close" onClick={onClose} aria-label="Close dialog">
            <X size={18} />
          </button>
        </div>
        <div className="tsp-modal-body">{children}</div>
        {footer && <div className="tsp-modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// Confirmation for destructive actions (delete/remove).
export function TspConfirmDialog({ open, title, message, confirmLabel = "Delete", busy = false, onConfirm, onCancel }) {
  return (
    <TspModal
      open={open}
      title={title}
      size="sm"
      onClose={busy ? undefined : onCancel}
      footer={
        <>
          <button type="button" className="tsp-btn tsp-btn-outline" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="tsp-btn tsp-btn-red" onClick={onConfirm} disabled={busy}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </>
      }
    >
      <p className="tsp-confirm-message">{message}</p>
    </TspModal>
  );
}
